import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  UserGroupIcon,
  TicketIcon,
  ArrowRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge } from '@/components/ui'
import { taskforceApi } from '@/api'
import type { TaskForce, Ticket } from '@/api/taskforce'
import type { DashboardTask } from '@/api/dashboard'
import { format, parseISO, differenceInDays, isPast } from 'date-fns'
import { it } from 'date-fns/locale'

interface Props {
  pendingTasks?: DashboardTask[]
}

export default function TaskForceWidget({ pendingTasks: initialTasks }: Props) {
  const [myTaskForces, setMyTaskForces] = useState<TaskForce[]>([])
  const [pendingTasks, setPendingTasks] = useState<DashboardTask[]>(initialTasks || [])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTaskForceData()
  }, [])

  const fetchTaskForceData = async () => {
    try {
      const taskforces = await taskforceApi.getMyTaskForces()
      setMyTaskForces(taskforces.slice(0, 3))

      if (!initialTasks) {
        // Fetch tickets assigned to me
        const allTickets: DashboardTask[] = []
        for (const tf of taskforces.slice(0, 3)) {
          try {
            const ticketsRes = await taskforceApi.getTickets(tf.id, { status: 'open,in_progress' })
            const tickets = (ticketsRes.results || [])
              .filter((t: Ticket) => t.status === 'open' || t.status === 'in_progress')
              .slice(0, 2)
              .map((t: Ticket) => ({
                id: t.id,
                title: t.title,
                status: t.status as 'open' | 'in_progress',
                due_date: t.due_date,
                taskforce_name: tf.name
              }))
            allTickets.push(...tickets)
          } catch (e) {
            // Continue with other task forces
          }
        }
        setPendingTasks(allTickets.slice(0, 5))
      }
    } catch (error) {
      console.error('Error fetching task force data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge size="sm" variant="info">Aperto</Badge>
      case 'in_progress':
        return <Badge size="sm" variant="warning">In corso</Badge>
      default:
        return <Badge size="sm">{status}</Badge>
    }
  }

  const getDueDateInfo = (dueDate: string | null) => {
    if (!dueDate) return null

    const date = parseISO(dueDate)
    const daysUntil = differenceInDays(date, new Date())
    const isOverdue = isPast(date)

    if (isOverdue) {
      return {
        text: 'Scaduto',
        icon: ExclamationTriangleIcon,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
      }
    }
    if (daysUntil <= 2) {
      return {
        text: `Scade ${format(date, 'd MMM', { locale: it })}`,
        icon: ClockIcon,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
      }
    }
    return {
      text: format(date, 'd MMM', { locale: it }),
      icon: ClockIcon,
      color: 'text-gray-500',
      bgColor: 'bg-gray-50',
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader title="Task Force" />
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </Card>
    )
  }

  return (
    <Card className="h-full">
      <CardHeader
        title={
          <div className="flex items-center gap-2">
            <UserGroupIcon className="h-5 w-5 text-orange-500" />
            <span>Le mie Task Force</span>
          </div>
        }
        action={
          <Link
            to="/taskforce"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            Tutte <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        }
      />

      {/* My Task Forces */}
      {myTaskForces.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            {myTaskForces.map((tf) => (
              <Link
                key={tf.id}
                to={`/taskforce/${tf.slug}`}
                className="flex items-center gap-2 px-3 py-2 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200 transition-colors"
              >
                <UserGroupIcon className="h-4 w-4 text-orange-600" />
                <span className="text-sm font-medium text-gray-900">{tf.name}</span>
                <Badge size="sm" variant="warning">{tf.tickets_count}</Badge>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Pending Tasks */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
          <TicketIcon className="h-4 w-4 text-gray-500" />
          Ticket da completare
        </p>

        {pendingTasks.length > 0 ? (
          <div className="space-y-2">
            {pendingTasks.map((task) => {
              const dueDateInfo = getDueDateInfo(task.due_date)

              return (
                <div
                  key={task.id}
                  className={`p-3 rounded-lg border transition-all hover:shadow-sm ${
                    dueDateInfo?.bgColor || 'bg-gray-50'
                  } border-gray-200`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {task.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">{task.taskforce_name}</span>
                        {getStatusBadge(task.status)}
                      </div>
                    </div>
                    {dueDateInfo && (
                      <div className={`flex items-center gap-1 ${dueDateInfo.color}`}>
                        <dueDateInfo.icon className="h-4 w-4" />
                        <span className="text-xs font-medium">{dueDateInfo.text}</span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : myTaskForces.length > 0 ? (
          <div className="text-center py-6 bg-green-50 rounded-lg">
            <CheckCircleIcon className="h-10 w-10 mx-auto mb-2 text-green-500" />
            <p className="text-sm text-green-700 font-medium">
              Nessun ticket in sospeso
            </p>
            <p className="text-xs text-green-600">
              Ottimo lavoro!
            </p>
          </div>
        ) : (
          <div className="text-center py-6">
            <UserGroupIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-500 mb-2">
              Non fai parte di nessuna Task Force
            </p>
            <Link
              to="/taskforce"
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
            >
              Scopri le Task Force <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
        )}
      </div>
    </Card>
  )
}
